/* ─────────────────────────────────────────────────────────
 *  MIDI DAW – Domain Types
 *
 *  All type definitions for the MIDI DAW feature: tracks,
 *  notes, transport, synth patches, and canvas geometry.
 * ────────────────────────────────────────────────────────── */

// ── MIDI Note (parsed from @tonejs/midi) ───────────────────

export interface DawNote {
  /** Stable per-note identity for selection/editing (assigned on load) */
  id?: string
  /** MIDI note number 0–127 */
  midi: number
  /** Note name e.g. "C4" */
  name: string
  /** Start time in ticks */
  ticks: number
  /** Duration in ticks */
  durationTicks: number
  /** Start time in seconds */
  time: number
  /** Duration in seconds */
  duration: number
  /** Velocity 0–127 */
  velocity: number
}

// ── Track ──────────────────────────────────────────────────

export interface DawTrack {
  /** Track index from MIDI file */
  index: number
  /** Track name from MIDI metadata */
  name: string
  /** MIDI channel (0-15) */
  channel: number
  /** Instrument name from MIDI program change */
  instrument: string
  /** All notes in this track */
  notes: DawNote[]
  /** Track color (auto-assigned) */
  color: string
  /** Is track muted */
  muted: boolean
  /** Is track soloed */
  solo: boolean
  /** Track volume 0–1 */
  volume: number
  /** Collapsed height in piano roll */
  collapsed: boolean
  /** Height in pixels when expanded */
  height: number
}

// ── Parsed MIDI file ───────────────────────────────────────

export interface DawMidiFile {
  /** Original file name */
  fileName: string
  /** Ticks per quarter note (PPQ) */
  ppq: number
  /** Duration in seconds */
  durationSeconds: number
  /** Duration in ticks */
  durationTicks: number
  /** BPM from tempo map (first tempo) */
  bpm: number
  /** Time signature numerator */
  timeSignatureNumerator: number
  /** Time signature denominator */
  timeSignatureDenominator: number
  /** All parsed tracks */
  tracks: DawTrack[]
}

// ── Transport (playback state) ─────────────────────────────

export type TransportState = 'stopped' | 'playing' | 'paused'

export interface TransportPosition {
  /** Current time in seconds */
  seconds: number
  /** Current time in ticks */
  ticks: number
  /** Current bar (1-based) */
  bar: number
  /** Current beat within bar (1-based) */
  beat: number
  /** Current sub-division pulse */
  pulse: number
}

// ── Synth patch (reuse & extend music sim synths) ──────────

export type DawSynthType = 'sine' | 'square' | 'triangle' | 'sawtooth' | 'pad' | 'lead' | 'string'

export interface DawSynthPatch {
  id: string
  name: string
  type: DawSynthType
  /** Oscillator detune in cents */
  detune: number
  /** Attack time in seconds */
  attack: number
  /** Decay time in seconds */
  decay: number
  /** Sustain level 0–1 */
  sustain: number
  /** Release time in seconds */
  release: number
  /** Filter cutoff frequency Hz (0 = no filter) */
  filterCutoff: number
  /** Filter resonance Q */
  filterQ: number
  /** Reverb wet 0–1 */
  reverbMix: number
  /** Chorus depth */
  chorusDepth: number
}

// ── Canvas / grid geometry ─────────────────────────────────

export interface PianoRollGeometry {
  /** Pixels per tick (horizontal zoom) */
  pxPerTick: number
  /** Pixels per MIDI note row (vertical zoom) */
  noteHeight: number
  /** Piano keyboard width in pixels */
  keyboardWidth: number
  /** Total MIDI note range */
  totalNotes: 128
  /** Header bar height in pixels */
  headerHeight: number
}

// ── Track colors ───────────────────────────────────────────

export const TRACK_COLORS = [
  '#58a6ff', // blue
  '#3fb950', // green
  '#f0883e', // orange
  '#a371f7', // purple
  '#f778ba', // pink
  '#e3b341', // yellow
  '#da3633', // red
  '#79c0ff', // cyan
  '#d2a8ff', // lavender
  '#56d364', // lime
  '#db6d28', // burnt orange
  '#bc8cff', // violet
] as const

// ── Default geometry ───────────────────────────────────────

export const DEFAULT_GEOMETRY: PianoRollGeometry = {
  // Base defaults already include an initial zoom-in feel:
  // H +2 steps => 0.15 * 1.1 * 1.1 = 0.1815, V +5 steps => 15.
  pxPerTick: 0.1815,
  noteHeight: 15,
  keyboardWidth: 56,
  totalNotes: 128,
  headerHeight: 28,
}

// ── Default synth patches ──────────────────────────────────

export const DEFAULT_PATCHES: Record<string, DawSynthPatch> = {
  sine: {
    id: 'sine', name: 'Clean Sine', type: 'sine',
    detune: 0, attack: 0.01, decay: 0.1, sustain: 0.7, release: 0.3,
    filterCutoff: 0, filterQ: 1, reverbMix: 0, chorusDepth: 0,
  },
  pad: {
    id: 'pad', name: 'Warm Pad', type: 'pad',
    detune: 12, attack: 0.4, decay: 0.6, sustain: 0.8, release: 1.2,
    filterCutoff: 2000, filterQ: 2, reverbMix: 0.4, chorusDepth: 0.3,
  },
  lead: {
    id: 'lead', name: 'Bright Lead', type: 'lead',
    detune: 5, attack: 0.01, decay: 0.2, sustain: 0.6, release: 0.15,
    filterCutoff: 4000, filterQ: 4, reverbMix: 0.15, chorusDepth: 0.1,
  },
  string: {
    id: 'string', name: 'Plucked String', type: 'string',
    detune: 0, attack: 0.005, decay: 0.3, sustain: 0.4, release: 0.5,
    filterCutoff: 3000, filterQ: 1.5, reverbMix: 0.25, chorusDepth: 0,
  },
  square: {
    id: 'square', name: 'Retro Square', type: 'square',
    detune: 0, attack: 0.01, decay: 0.15, sustain: 0.5, release: 0.2,
    filterCutoff: 0, filterQ: 1, reverbMix: 0, chorusDepth: 0,
  },
  sawtooth: {
    id: 'sawtooth', name: 'Fat Saw', type: 'sawtooth',
    detune: 8, attack: 0.02, decay: 0.3, sustain: 0.6, release: 0.25,
    filterCutoff: 3500, filterQ: 3, reverbMix: 0.1, chorusDepth: 0.2,
  },
}

// ── MIDI note name helpers ─────────────────────────────────

const NOTE_NAMES_SHARP = ['C', 'C♯', 'D', 'D♯', 'E', 'F', 'F♯', 'G', 'G♯', 'A', 'A♯', 'B'] as const

export function midiToNoteName(midi: number): string {
  const octave = Math.floor(midi / 12) - 1
  const noteIdx = midi % 12
  return `${NOTE_NAMES_SHARP[noteIdx]}${octave}`
}

export function midiToFrequency(midi: number): number {
  return 440 * Math.pow(2, (midi - 69) / 12)
}

export function isBlackKey(midi: number): boolean {
  const n = midi % 12
  return n === 1 || n === 3 || n === 6 || n === 8 || n === 10
}
